import { environment } from "../../environments/environment";
import { showToast } from "./ui-utils";

/**
 * The add-in's signed-in session, shared by both ways in -- email and password, and Sign in with
 * Xero -- so they leave exactly the same state behind.
 */

/** Seconds of slack, so a token about to expire is not treated as good. */
const EXPIRY_SLACK_SECONDS = 60;

/** The exp claim of a JWT, or null when the token cannot be read. */
function tokenExpiry(token: string): number | null {
    try {
        const payload = token.split(".")[1];
        if (!payload) return null;
        const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "="));
        const exp = JSON.parse(json)?.exp;
        return typeof exp === "number" ? exp : null;
    } catch {
        return null;
    }
}

/**
 * True when a stored session can be used without signing in again, as the web app skips its
 * login page for a signed-in user. A token that cannot be read, or has expired, is not one.
 */
export function hasValidSession(): boolean {
    const token = localStorage.getItem("AccessToken");
    if (!token || !localStorage.getItem("User")) return false;
    const exp = tokenExpiry(token);
    return exp !== null && exp - EXPIRY_SLACK_SECONDS > Date.now() / 1000;
}

/**
 * The user as the rest of the add-in reads it. api/token returns camelCase; the Xero sign-in
 * returns the server's PascalCase (Id, OrganizationId, …). Both become camelCase here.
 */
function normaliseUser(raw: any): any {
    const user: any = {};
    Object.keys(raw || {}).forEach(key => {
        const camel = key.charAt(0).toLowerCase() + key.slice(1);
        if (user[camel] === undefined) user[camel] = raw[key];
    });
    return user;
}

/** Store a fresh session and resolve the organisation's display name. Returns the user. */
export async function storeSession(accessToken: string, rawUser: any): Promise<any> {
    const user = normaliseUser(rawUser);
    localStorage.setItem("AccessToken", accessToken);
    localStorage.setItem("User", JSON.stringify(user));

    if (user.organizationId) {
        localStorage.setItem("SelectedOrganization", user.organizationId);
        try {
            const res = await fetch(environment.baseUrl + "api/Organisation/OrganisationGet", {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (res.ok) {
                const orgs = (await res.json())?.data || [];
                const org = orgs.find((o: any) => o.organisationID === user.organizationId);
                if (org?.organisationName) {
                    user.organizationName = org.organisationName;
                    localStorage.setItem("User", JSON.stringify(user));
                    localStorage.setItem("organizationName", org.organisationName);
                    localStorage.setItem("SelectedOrganizationName", org.organisationName);
                }
            }
        } catch {
            // The name is display only.
        }
    }

    return user;
}

/**
 * Warn when the organisation's accounting connection has dropped, as the web app does at sign in.
 * Otherwise the first anyone hears of it is a report failing. Never blocks and never throws.
 */
export async function warnIfDisconnected(user: any): Promise<void> {
    const token = localStorage.getItem("AccessToken");
    if (!token || !user?.organizationId) return;
    try {
        const url = `${environment.baseUrl}api/Organisation/GetOrganisationsDetailsByUsersLoginId` +
            `?OrganisationId=${encodeURIComponent(user.organizationId)}&userId=${encodeURIComponent(user.id || "")}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const org = (await res.json())?.data;
        if (org && org.organisationConnected === false) {
            const name = org.syncOrgName || org.name || "This organisation";
            const domain = org.domain || "its accounting system";
            showToast(`${name} is not connected to ${domain}. Reconnect it in Synced before running reports.`, true);
        }
    } catch {
        // A warning, not a gate.
    }
}
